import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import api, { fmtIDR } from "../../lib/api";
import { inputCls, Field, Panel, btnPrimary, errMsg } from "./ui";

function MinRow({ p, gameName, catMin, onSaved }) {
  const [v, setV] = useState(p.min_order_idr ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(p.min_order_idr ?? ""); }, [p.min_order_idr]);
  const custom = (p.min_order_idr ?? catMin) !== catMin;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/products/${p.id}/min-order`, { min_order_idr: Number(v) || 0 });
      toast.success(`Minimal ${p.name} disimpan`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <tr data-testid={`pmin-row-${p.id}`} className="border-b border-[#35353B]">
      <td className="py-2 pr-3 text-[#A8A8AD]">{gameName}</td>
      <td className="py-2 pr-3 text-[#F5F3ED]">{p.name}{custom && <span data-testid={`pmin-custom-${p.id}`} className="ml-2 text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37] rounded-full px-2 py-0.5">Khusus</span>}</td>
      <td className="py-2 pr-3 text-right text-[#D4AF37]">{fmtIDR(p.sell_price_idr)}</td>
      <td className="py-2 pr-3"><input data-testid={`pmin-input-${p.id}`} type="number" min="0" className={`${inputCls} !py-1.5 w-32`} value={v} onChange={(e) => setV(e.target.value)} /></td>
      <td className="py-2"><button data-testid={`pmin-save-${p.id}`} onClick={save} disabled={busy || String(v) === String(p.min_order_idr ?? "")} className="text-xs font-bold text-[#D4AF37] hover:text-[#F2D98D] disabled:opacity-30 transition-colors">{busy ? "..." : "Simpan"}</button></td>
    </tr>
  );
}

export default function ProductMinOrderPanel({ products, games, cats, onSaved }) {
  const [catMins, setCatMins] = useState({});
  const [gameMins, setGameMins] = useState({});
  const [cat, setCat] = useState("coins");
  const [gameId, setGameId] = useState("");
  const [amount, setAmount] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const loadMins = () => api.get("/settings/category-min").then((r) => { setCatMins(r.data.categories); setGameMins(r.data.games); });
  useEffect(() => { loadMins(); }, []);
  const bulkOf = (gid, slug) => gameMins[`${gid}:${slug}`] ?? catMins[slug] ?? 0;
  useEffect(() => { if (catMins[cat] !== undefined) setAmount(gameId ? bulkOf(gameId, cat) : catMins[cat]); }, [cat, gameId, catMins, gameMins]); // eslint-disable-line react-hooks/exhaustive-deps
  const catName = cats.find((c) => c.slug === cat)?.name || cat;
  const scopeName = gameId ? `${catName} ${games.find((g) => g.id === gameId)?.name || ""}` : catName;
  const applyBulk = async () => {
    const v = Number(amount) || 0;
    if (!window.confirm(`Terapkan minimal ${fmtIDR(v)} ke semua produk ${scopeName}? Produk dengan nilai khusus tidak diubah.`)) return;
    setBusy(true);
    try {
      const r = await api.put("/products-min-order", { min_order_idr: v, category_slug: cat, game_id: gameId || null });
      toast.success(`Diterapkan ke ${r.data.updated} produk${r.data.custom_kept ? `, ${r.data.custom_kept} produk khusus dipertahankan` : ""}`);
      loadMins(); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const gameName = (id) => games.find((g) => g.id === id)?.name || "-";
  const term = q.trim().toLowerCase();
  const rows = term ? products.filter((p) => !p.deleted && (p.name.toLowerCase().includes(term) || gameName(p.game_id).toLowerCase().includes(term))) : [];
  return (
    <Panel title="Minimal Pembelian Produk" testId="product-min-panel">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end mb-6 pb-6 border-b border-[#35353B]">
        <Field label="Game">
          <select data-testid="pmin-bulk-game" className={inputCls} value={gameId} onChange={(e) => setGameId(e.target.value)}>
            <option value="">Semua game</option>
            {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="Kategori">
          <select data-testid="pmin-bulk-category" className={inputCls} value={cat} onChange={(e) => setCat(e.target.value)}>
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Minimal massal (IDR)">
          <input data-testid="pmin-bulk-input" type="number" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <button data-testid="pmin-bulk-apply-btn" onClick={applyBulk} disabled={busy || amount === ""} className={btnPrimary}>{busy ? "Menerapkan..." : `Terapkan ke Semua ${scopeName}`}</button>
        <p data-testid="pmin-bulk-current" className="text-xs text-[#A8A8AD]">Nilai massal saat ini: {fmtIDR(gameId ? bulkOf(gameId, cat) : catMins[cat] || 0)}. Produk bertanda <span className="text-[#D4AF37]">Khusus</span> tidak ikut berubah.</p>
      </div>
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8AD]" />
        <input data-testid="pmin-search-input" placeholder="Cari game atau produk, mis. Aion 2 atau Siel" className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {term && (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-[#A8A8AD] border-b border-[#35353B]"><th className="py-2">Game</th><th>Produk</th><th className="text-right pr-3">Harga</th><th>Minimal</th><th /></tr></thead>
            <tbody>{rows.slice(0, 60).map((p) => <MinRow key={p.id} p={p} gameName={gameName(p.game_id)} catMin={bulkOf(p.game_id, p.category_slug)} onSaved={onSaved} />)}</tbody>
          </table>
          {rows.length === 0 && <p data-testid="pmin-no-results" className="text-xs text-[#A8A8AD] py-3">Tidak ada produk yang cocok.</p>}
          {rows.length > 60 && <p className="text-xs text-[#A8A8AD] py-3">Menampilkan 60 dari {rows.length} produk, persempit pencarian.</p>}
        </div>
      )}
    </Panel>
  );
}
